import axios from "axios";
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Lock,
  Eye,
  EyeOff,
  AlertTriangle,
  Trash2,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import { toast } from "react-toastify";

const DeleteAccount = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);

  // Login Function
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `https://shayariserver.testsetu.in/api/v1/auth/login`,
        null,
        {
          params: {
            email: username,
            password: password,
          },
        },
      );

      if (response.data.success) {
        setToken(response.data.token);
        setIsAuthenticated(true);
        toast.success("Identity verified successfully");
      } else {
        toast.error("Login failed. Please check your credentials.");
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Login failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Delete Function
  const handleDeleteAccount = async () => {
    if (
      !window.confirm(
        "Are you absolutely sure you want to delete your account? This cannot be undone.",
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(
        `https://shayariserver.testsetu.in/api/v1/auth/deleteAccount`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      toast.success("Account deleted successfully");
      // Redirect or handle post-deletion logic
      window.location.href = "/";
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message ||
          "Failed to delete account. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4">
      {/* Background decorations */}
      <div className="pointer-events-none absolute left-0 top-0 h-full w-full overflow-hidden">
        <div className="absolute -right-40 -top-40 h-80 w-80 rounded-full bg-purple-200 opacity-20 blur-3xl" />
        <div className="absolute -left-20 top-40 h-72 w-72 rounded-full bg-indigo-200 opacity-20 blur-3xl" />
        <div className="absolute bottom-20 right-20 h-96 w-96 rounded-full bg-pink-200 opacity-20 blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="overflow-hidden rounded-2xl border border-white/50 bg-white/80 shadow-xl backdrop-blur-xl">
          {/* Header */}
          <div className="relative overflow-hidden bg-gradient-to-r from-red-500 to-pink-600 p-6 text-center text-white">
            <div className="absolute inset-0 bg-black/10" />
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="relative z-10 flex flex-col items-center"
            >
              <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-white/20 shadow-inner backdrop-blur-sm">
                <Trash2 size={32} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold">Delete Account</h2>
              <p className="mt-1 text-sm text-red-100">
                Shayari App Configuration
              </p>
            </motion.div>
          </div>

          <div className="p-8">
            <AnimatePresence mode="wait">
              {!isAuthenticated ? (
                <motion.div
                  key="login-form"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="mb-8 text-center">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Verify Your Identity
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Please log in to proceed with account deletion.
                    </p>
                  </div>

                  <form onSubmit={handleLogin} className="space-y-5">
                    <div className="space-y-1">
                      <label className="ml-1 text-xs font-medium uppercase tracking-wider text-gray-500">
                        Username
                      </label>
                      <div className="group relative">
                        <User
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-indigo-500"
                          size={18}
                        />
                        <input
                          type="text"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          className="w-full rounded-xl border border-gray-200 bg-gray-50 py-3 pl-10 pr-4 text-sm text-gray-800 shadow-sm transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                          placeholder="Enter your username"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="ml-1 text-xs font-medium uppercase tracking-wider text-gray-500">
                        Password
                      </label>
                      <div className="group relative">
                        <Lock
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-indigo-500"
                          size={18}
                        />
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full rounded-xl border border-gray-200 bg-gray-50 py-3 pl-10 pr-12 text-sm text-gray-800 shadow-sm transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                        >
                          {showPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={loading}
                      className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 font-medium text-white shadow-lg shadow-indigo-500/30 transition-all hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {loading ? (
                        <Loader2 size={20} className="animate-spin" />
                      ) : (
                        <>
                          <ShieldCheck size={20} />
                          <span>Verify & Continue</span>
                        </>
                      )}
                    </motion.button>
                  </form>
                </motion.div>
              ) : (
                <motion.div
                  key="confirmation"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex flex-col items-center space-y-6 text-center">
                    <div className="flex h-16 w-16 animate-pulse items-center justify-center rounded-full bg-red-100">
                      <AlertTriangle size={32} className="text-red-500" />
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        Final Warning
                      </h3>
                      <p className="mx-auto mt-2 max-w-xs text-sm text-gray-500">
                        You are about to permanently delete the account for{" "}
                        <span className="font-semibold text-gray-900">
                          {username}
                        </span>
                        .
                      </p>
                    </div>

                    <div className="w-full rounded-lg border border-red-100 bg-red-50 p-4 text-left">
                      <h4 className="mb-2 text-sm font-semibold text-red-800">
                        What will happen:
                      </h4>
                      <ul className="list-inside list-disc space-y-1 text-xs text-red-700">
                        <li>All personal data will be erased.</li>
                        <li>
                          You will lose access to all services immediately.
                        </li>
                        <li>This action cannot be undone.</li>
                      </ul>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02, backgroundColor: "#dc2626" }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleDeleteAccount}
                      disabled={loading}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-3.5 font-bold text-white shadow-lg shadow-red-500/30 transition-colors disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {loading ? (
                        <Loader2 size={20} className="animate-spin" />
                      ) : (
                        <>
                          <Trash2 size={20} />
                          <span>DELETE MY ACCOUNT</span>
                        </>
                      )}
                    </motion.button>

                    <button
                      onClick={() => setIsAuthenticated(false)}
                      className="text-sm font-medium text-gray-400 transition-colors hover:text-gray-600"
                      disabled={loading}
                    >
                      Cancel and Go Back
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 bg-gray-50 p-4 text-center">
            <p className="text-xs text-gray-400">
              © {new Date().getFullYear()} Setu Shayari. All rights reserved.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DeleteAccount;
